/**
 * Reports Page
 * Main reports landing page showing all report categories
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  MonitorHeart as OperationsMonitorIcon,
  SettingsBackupRestore as OverridesIcon,
  HealthAndSafety as MarketplaceHealthIcon,
  Person as GhostPerformanceIcon,
  People as AssetBehaviorIcon,
  Security as ComplianceRiskIcon,
  Computer as SystemHealthIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner } from '../../components/common';
import { Card, CardContent, PrimaryButton } from '../../components/ui';
import { ReportsService } from '../../services/reports/ReportsService';
import type { ReportCategory } from '../../types/reports';

const iconMap: Record<string, React.ReactNode> = {
  monitor: <OperationsMonitorIcon sx={{ fontSize: 40 }} />,
  override: <OverridesIcon sx={{ fontSize: 40 }} />,
  health: <MarketplaceHealthIcon sx={{ fontSize: 40 }} />,
  ghost: <GhostPerformanceIcon sx={{ fontSize: 40 }} />,
  asset: <AssetBehaviorIcon sx={{ fontSize: 40 }} />,
  compliance: <ComplianceRiskIcon sx={{ fontSize: 40 }} />,
  system: <SystemHealthIcon sx={{ fontSize: 40 }} />,
};

const getMetricColor = (theme: any, color?: string) => {
  switch (color) {
    case 'success':
      return theme.palette.success.main;
    case 'error':
      return theme.palette.error.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'info':
      return theme.palette.info.main;
    default:
      return theme.palette.text.secondary;
  }
};

const ReportsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [reportCategories, setReportCategories] = useState<ReportCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchReportCategories = async () => {
      try {
        setIsLoading(true);
        const categories = await ReportsService.getReportCategories();
        setReportCategories(categories);
      } catch (error) {
        console.error('Failed to fetch report categories:', error);
        setReportCategories([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReportCategories();
  }, []);

  const handleViewReport = (path: string) => {
    navigate(path);
  };

  if (isLoading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
          <LoadingSpinner />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>


      {/* Page Header */}
      <Box sx={{ marginBottom: theme.spacing(4) }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(1),
          }}
        >
          Reports
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: theme.palette.text.secondary }}
        >
          Access detailed reports and analytics for your platform.
        </Typography>
      </Box>

      {/* Report Cards Grid */}
      <Grid container spacing={3}>
        {reportCategories.map((category, index) => (
          <Grid item xs={12} sm={6} md={4} key={category.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.customColors.tableBorder}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: theme.shadows[8],
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardContent
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  padding: theme.spacing(3),
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    marginBottom: theme.spacing(2),
                    color: theme.palette.primary.main,
                  }}
                >
                  {iconMap[category.icon] || <OperationsMonitorIcon sx={{ fontSize: 40 }} />}
                </Box>

                {/* Title */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: theme.typography.h6.fontWeight,
                    color: theme.palette.text.primary,
                    marginBottom: theme.spacing(1),
                    fontSize: theme.typography.h6.fontSize,
                  }}
                >
                  {category.title}
                </Typography>

                {/* Description */}
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    marginBottom: theme.spacing(2),
                    flex: 1,
                    fontSize: theme.typography.body2.fontSize,
                    lineHeight: 1.6,
                  }}
                >
                  {category.description}
                </Typography>

                {/* Metric (if available) */}
                {category.metric && (
                  <Box sx={{ marginBottom: theme.spacing(2) }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: theme.typography.subtitle2.fontWeight,
                        color: getMetricColor(theme, category.metricColor),
                        fontSize: theme.typography.body2.fontSize,
                      }}
                    >
                      {category.metric}
                    </Typography>
                  </Box>
                )}

                {/* View Report Button */}
                <PrimaryButton
                  onClick={() => handleViewReport(category.path)}
                  disabled={index !== 0}
                  sx={{
                    marginTop: 'auto',
                  }}
                >
                  {index === 0 ? 'View Report' : 'Coming Soon'}
                </PrimaryButton>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </MainLayout>
  );
};

export default ReportsPage;

